/**
 * /blog — public blog index. Lists published posts only.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import AllArticlesIndex from "@/components/AllArticlesIndex";
import { useSEO, SEO_DEFAULTS } from "@/lib/useSEO";

const API = process.env.REACT_APP_BACKEND_URL;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch { return ""; }
}

export default function BlogIndex() {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Feb 2026 — every public page must self-declare a canonical + og:url
  // on the AWARE domain (see useSEO.js). The blog INDEX belongs to the
  // sitemap and needs an unambiguous self-reference so Google doesn't
  // conflate it with any single article.
  useSEO({
    title: "Blog",
    description: "Plain-English water testing articles for Australian homes — tank water, bore water, safety guides.",
    url: `${SEO_DEFAULTS.baseUrl}/blog`,
  });

  useEffect(() => {
    fetch(`${API}/api/blog/posts?limit=24`)
      .then((r) => r.json())
      .then((d) => {
        setPosts(d.posts || []);
        setTotal(typeof d.total === "number" ? d.total : (d.posts || []).length);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  // Phase 2L-A98 — Load More so the public archive can grow past
  // the first 24 posts. Uses the existing `skip` query param on
  // `/api/blog/posts`. Stops appending when `posts.length >= total`.
  const loadMore = () => {
    setLoadingMore(true);
    fetch(`${API}/api/blog/posts?limit=24&skip=${posts.length}`)
      .then((r) => r.json())
      .then((d) => {
        setPosts((prev) => [...prev, ...((d && d.posts) || [])]);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  };

  const hasMore = posts.length < total;

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-28 lg:pt-36 pb-12 lg:pb-16" data-testid="blog-index">
        <header className="mb-10 lg:mb-12">
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900">Aware Blog</h1>
          <p className="mt-3 text-base text-slate-600 max-w-2xl">
            Practical guides and lab insights for tank, bore and household water — from the team at Aware Water Testing.
          </p>
        </header>

        {loading && <div className="text-slate-500" data-testid="blog-loading">Loading…</div>}

        {!loading && posts.length === 0 && (
          <div className="rounded-2xl bg-white ring-1 ring-slate-200 p-8 text-center text-slate-600" data-testid="blog-empty">
            New articles are on the way — check back soon.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-sky-300 hover:shadow-lg transition-all overflow-hidden flex flex-col"
            >
              {/* Phase 2L-A36 — calm in-house fallback image when a post
                  has no `featured_image_url`. GetAutoSEO-published posts
                  continue to use their real hero exactly as before. */}
              <picture data-testid={`blog-card-${p.slug}-image`}>
                {!p.featured_image_url && (
                  <source srcSet="/img/v2/blog-fallback.webp" type="image/webp" />
                )}
                <img
                  src={p.featured_image_url || "/img/v2/blog-fallback.jpg"}
                  alt={p.featured_image_url ? p.title : ""}
                  loading="lazy"
                  decoding="async"
                  width="1200"
                  height="675"
                  className="w-full h-44 object-cover"
                />
              </picture>
              <div className="p-5 flex-1 flex flex-col">
                {p.category && (
                  <div className="text-[11px] uppercase tracking-[0.18em] text-cyan-700 font-bold mb-1">{p.category}</div>
                )}
                <h2 className="text-lg font-semibold text-slate-900 group-hover:text-[#1F4E79] leading-snug">
                  {p.title}
                </h2>
                {p.excerpt && <p className="mt-2 text-sm text-slate-600 leading-snug line-clamp-3">{p.excerpt}</p>}
                <div className="mt-3 text-xs text-slate-500">{fmtDate(p.published_at)}</div>
              </div>
            </Link>
          ))}
        </div>

        {/* Phase 2L-A98 — Load More button. Renders only when the
            server reports more published posts than the page has
            currently fetched. Keeps the public archive accessible
            beyond the first page without forcing pagination URLs. */}
        {!loading && hasMore && (
          <div className="mt-10 text-center">
            <button
              type="button"
              onClick={loadMore}
              disabled={loadingMore}
              data-testid="blog-load-more"
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 bg-white px-6 py-3 text-sm font-medium text-slate-700 hover:border-cyan-400 hover:text-cyan-700 transition disabled:opacity-50"
            >
              {loadingMore ? "Loading…" : `Load more (${total - posts.length} remaining)`}
            </button>
          </div>
        )}

        {/* Sep 2026 — crawlable full archive so every published post has
            a hub link even when it sits behind "Load more". */}
        <AllArticlesIndex
          heading="All articles"
          blurb="The complete archive, grouped by topic."
          testid="blog-all-articles"
        />
      </main>
      <Footer />
    </div>
  );
}
